"use client";

import { useEffect, useState } from "react";
import { fetchPortalV2LocationByIdentifier, fetchPortalV2LocationByLookup } from "./client";
import type { PortalV2Location } from "./types";

type UseLocationState = {
	data: PortalV2Location | null;
	isPending: boolean;
	error: unknown;
};

export function usePortalV2LocationByLookup({
	lookupId,
	enabled = true,
}: {
	lookupId: string;
	enabled?: boolean;
}): UseLocationState {
	const [state, setState] = useState<UseLocationState>({
		data: null,
		isPending: enabled,
		error: null,
	});

	useEffect(() => {
		if (!enabled) {
			setState({ data: null, isPending: false, error: null });
			return;
		}

		let isMounted = true;

		const fetchData = async () => {
			try {
				setState({ data: null, isPending: true, error: null });
				const result = await fetchPortalV2LocationByLookup({ lookupId });
				if (isMounted) {
					setState({ data: result, isPending: false, error: null });
				}
			} catch (err) {
				if (isMounted) {
					setState({ data: null, isPending: false, error: err });
				}
			}
		};

		fetchData();

		return () => {
			isMounted = false;
		};
	}, [lookupId, enabled]);

	return state;
}

export function usePortalV2LocationByIdentifier({
	identifier,
	enabled = true,
}: {
	identifier: string;
	enabled?: boolean;
}): UseLocationState {
	const [state, setState] = useState<UseLocationState>({
		data: null,
		isPending: enabled,
		error: null,
	});

	useEffect(() => {
		if (!enabled) {
			setState({ data: null, isPending: false, error: null });
			return;
		}

		let isMounted = true;

		const fetchData = async () => {
			try {
				setState({ data: null, isPending: true, error: null });
				const result = await fetchPortalV2LocationByIdentifier({ identifier });
				if (isMounted) {
					setState({ data: result, isPending: false, error: null });
				}
			} catch (err) {
				if (isMounted) {
					setState({ data: null, isPending: false, error: err });
				}
			}
		};

		fetchData();

		return () => {
			isMounted = false;
		};
	}, [identifier, enabled]);

	return state;
}
